/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useAIModels from "../hooks/useAIModels";
import axios from "axios";
import UIButton from "../components/Button";
import UIPopup from "../components/UIPopup";
import { storage } from "../utils/storage";
import CardModel from "../components/CardModel";
import { AIModel } from "../types/AIModel";
import { css } from "@emotion/react";
import { useUIStore } from "../store/uiStore";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import katex from "katex";
import "katex/dist/katex.min.css";
import MermaidRenderer from "../components/MermaidRenderer";
import MarkmapRenderer from "../components/MarkmapRenderer";
import DOMPurify from "dompurify";
import TikzRenderer from "../components/TikzRenderer";
import CodeBlock from "../components/CodeBlock";
import { MediaQuickActions, QuickAction } from "../components/MediaQuickActions";
const baseUrl = process.env.REACT_APP_API_CONFIG;
const baseHost = process.env.REACT_APP_API_HOST;

// Component to render image with signed URL
interface SignedImageProps {
  mediaFileDocumentId: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  onSignedUrlReady?: (signedUrl: string) => void;
}

const SignedImage: React.FC<SignedImageProps> = ({
  mediaFileDocumentId,
  alt,
  className,
  style,
  onClick,
  onSignedUrlReady
}) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Use ref to store callback to avoid infinite loop
  const onSignedUrlReadyRef = useRef(onSignedUrlReady);
  onSignedUrlReadyRef.current = onSignedUrlReady;

  useEffect(() => {
    // Skip if already loaded
    if (signedUrl) return;

    let isCancelled = false;

    const fetchSignedUrl = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/media/${mediaFileDocumentId}/signed-url`,
          {
            headers: { Authorization: `Bearer ${storage.getJwtToken()}` },
          }
        );

        if (isCancelled) return;

        const url = response.data?.data?.signed_url;
        if (url) {
          setSignedUrl(url);
          onSignedUrlReadyRef.current?.(url);
        } else {
          setError(true);
        }
      } catch (err: any) {
        if (isCancelled) return;
        console.error("Failed to fetch signed URL for image:", err);
        setError(true);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchSignedUrl();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFileDocumentId]); // Only re-fetch when mediaFileDocumentId changes

  if (loading) {
    return (
      <div
        className={className}
        style={{ ...style, backgroundColor: '#2F3036', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}
      >
        <div className="flex gap-1">
          <div className="loading-dot loading-dot-1"></div>
          <div className="loading-dot loading-dot-2"></div>
          <div className="loading-dot loading-dot-3"></div>
        </div>
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div
        className={className}
        style={{ ...style, backgroundColor: '#2F3036', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: '#888' }}
      >
        Failed to load image
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
    />
  );
};

// CSS for loading animation
const loadingDotsStyle = `
  @keyframes loadingPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  
  .loading-dot {
    width: 8px;
    height: 8px;
    background-color: white;
    border-radius: 50%;
  }
  
  .loading-dot-1 {
    animation: loadingPulse 1.4s infinite;
    animation-delay: 0s;
  }
  
  .loading-dot-2 {
    animation: loadingPulse 1.4s infinite;
    animation-delay: 0.2s;
  }
  
  .loading-dot-3 {
    animation: loadingPulse 1.4s infinite;
    animation-delay: 0.4s;
  }
  
  .file-drop-area {
    border: 2px dashed #444;
    border-radius: 10px;
    background-color: rgba(30, 30, 30, 0.5);
    transition: all 0.3s ease;
  }
    
  .attachment-preview {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    object-fit: cover;
  }

    .generated-image {
    max-width: 100%;
    border-radius: 12px;
    margin: 8px 0;
  }

  /* Processing indicator animation */
  @keyframes processingPulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
    }
  }

  @keyframes uploadingPulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(234, 179, 8, 0);
    }
  }

  .status-processing {
    animation: processingPulse 2s infinite;
  }

  .status-uploading {
    animation: uploadingPulse 1.5s infinite;
  }
`;

/**
 * Convert LaTeX delimiters from \(...\) to $...$ and \[...\] to $$...$$
 * This is needed because AI models often output \(...\) format but remark-math expects $...$
 */
function convertLatexDelimiters(content: string): string {
  if (!content) return content;

  // Convert block math: \[...\] to $$...$$
  let result = content.replace(/\\\[([\s\S]*?)\\\]/g, (match, latex) => {
    return `$$${latex}$$`;
  });

  // Convert inline math: \(...\) to $...$
  // Be careful not to convert already converted or escaped content
  result = result.replace(/\\\(([\s\S]*?)\\\)/g, (match, latex) => {
    return `$${latex}$`;
  });

  return result;
}

/**
 * Render LaTeX math expressions in HTML content using KaTeX
 * Supports both inline ($...$) and block ($$...$$) math
 */
function renderLatexInHtml(html: string): string {
  // Skip if already contains rendered KaTeX
  if (html.includes('class="katex"')) {
    return html;
  }

  // First, render block math ($$...$$)
  // Use a more permissive regex that handles multiline and various LaTeX content
  let result = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
      });
    } catch (e) {
      console.warn('KaTeX block render error:', e);
      return match; // Return original on error
    }
  });

  // Then, render inline math ($...$)
  // Match $ followed by content (including backslashes for LaTeX commands) until next $
  // Use a more permissive pattern that allows LaTeX commands like \frac, \pi, etc.
  result = result.replace(/\$([^$]+?)\$/g, (match, latex) => {
    // Skip if:
    // - Already rendered (contains katex class)
    // - Looks like currency ($5, $10.50)
    // - Empty content
    const trimmed = latex.trim();
    if (!trimmed || /^\d/.test(trimmed) || latex.includes('class="katex"')) {
      return match;
    }

    try {
      return katex.renderToString(trimmed, {
        displayMode: false,
        throwOnError: false,
        trust: true,
      });
    } catch (e) {
      console.warn('KaTeX inline render error:', e, 'latex:', latex);
      return match; // Return original on error
    }
  });

  return result;
}

// Component to render pre-converted HTML from backend
// Handles special blocks: mermaid, markmap, and code with syntax highlighting
interface HtmlContentRendererProps {
  html: string;
  onMermaid: (chart: string) => React.ReactNode;
  onMarkmap: (markdown: string) => React.ReactNode;
}

const HtmlContentRenderer: React.FC<HtmlContentRendererProps> = ({
  html,
  onMermaid,
  onMarkmap,
}) => {
  // Parse HTML to extract special blocks (mermaid, markmap, code)
  const renderContent = () => {
    const elements: React.ReactNode[] = [];
    let keyIndex = 0;

    // First, render LaTeX math expressions in the HTML
    const htmlWithLatex = renderLatexInHtml(html);

    // Regex patterns for special blocks
    const mermaidRegex = /<div class="mermaid-block" data-chart="([^"]*)"[^>]*>[\s\S]*?<\/div>/g;
    const markmapRegex = /<div class="markmap-block" data-markdown="([^"]*)"[^>]*>[\s\S]*?<\/div>/g;
    // Match <pre><code class="language-xxx">...</code></pre> patterns
    const codeBlockRegex = /<pre><code(?:\s+class="language-([^"]*)")?>([\s\S]*?)<\/code><\/pre>/g;

    // Collect all matches with their positions
    type BlockMatch = {
      type: 'mermaid' | 'markmap' | 'code';
      fullMatch: string;
      data: string;
      language?: string;
      index: number;
    };
    const allMatches: BlockMatch[] = [];

    let match;
    while ((match = mermaidRegex.exec(htmlWithLatex)) !== null) {
      allMatches.push({
        type: 'mermaid',
        fullMatch: match[0],
        data: decodeURIComponent(match[1]),
        index: match.index,
      });
    }

    while ((match = markmapRegex.exec(htmlWithLatex)) !== null) {
      allMatches.push({
        type: 'markmap',
        fullMatch: match[0],
        data: decodeURIComponent(match[1]),
        index: match.index,
      });
    }

    // Extract code blocks for syntax highlighting
    while ((match = codeBlockRegex.exec(htmlWithLatex)) !== null) {
      const language = match[1] || '';
      // Decode HTML entities in code content
      const codeContent = match[2]
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

      allMatches.push({
        type: 'code',
        fullMatch: match[0],
        data: codeContent,
        language: language,
        index: match.index,
      });
    }

    // Sort by position
    allMatches.sort((a, b) => a.index - b.index);

    // If no special blocks, render entire HTML (with LaTeX already rendered)
    if (allMatches.length === 0) {
      return (
        <div
          className="markdown-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlWithLatex) }}
        />
      );
    }

    // Split HTML by special blocks and render
    let lastIndex = 0;
    for (const blockMatch of allMatches) {
      // Add HTML before this block
      if (blockMatch.index > lastIndex) {
        const beforeHtml = htmlWithLatex.substring(lastIndex, blockMatch.index);
        if (beforeHtml.trim()) {
          elements.push(
            <div
              key={`html-${keyIndex++}`}
              className="markdown-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(beforeHtml) }}
            />
          );
        }
      }

      // Add the special block component
      if (blockMatch.type === 'mermaid') {
        elements.push(
          <div key={`mermaid-${keyIndex++}`}>
            {onMermaid(blockMatch.data)}
          </div>
        );
      } else if (blockMatch.type === 'markmap') {
        elements.push(
          <div key={`markmap-${keyIndex++}`}>
            {onMarkmap(blockMatch.data)}
          </div>
        );
      } else if (blockMatch.type === 'code') {
        elements.push(
          <CodeBlock
            key={`code-${keyIndex++}`}
            code={blockMatch.data}
            language={blockMatch.language}
          />
        );
      }

      lastIndex = blockMatch.index + blockMatch.fullMatch.length;
    }

    // Add any remaining HTML after the last block
    if (lastIndex < htmlWithLatex.length) {
      const afterHtml = htmlWithLatex.substring(lastIndex);
      if (afterHtml.trim()) {
        elements.push(
          <div
            key={`html-${keyIndex++}`}
            className="markdown-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(afterHtml) }}
          />
        );
      }
    }

    return <>{elements}</>;
  };

  return <div className="html-content-wrapper">{renderContent()}</div>;
};

interface MediaFile {
  id: string;
  documentId: string;
  original_filename: string;
  mime_type: string;
  media_type: 'pdf' | 'audio' | 'video' | 'image' | 'markdown';
  file_size_bytes?: number;
  // For markdown files: content to render inline
  content?: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  attachments?: Array<{
    type: string;
    url: string;
    name: string;
  }>;
  documentId?: string;
  message_type?: "text" | "image";
  images?: Array<{
    message: ReactNode;
    id: number;
    documentId: string;
    url: string;
    formats: {
      thumbnail?: {
        url: string;
      };
      small?: {
        url: string;
      };
      medium?: {
        url: string;
      };
      large?: {
        url: string;
      };
    };
  }>;
  media_files?: MediaFile[];
}

export default function Chat() {
  interface RouteParams extends Record<string, string | undefined> {
    modelId?: string;
    documentId?: string;
  }

  type UploadedFile = {
    type: string;
    url: string;
    name: string;
    id: number;
    documentId: string;
    // For media files (PDF, audio, video)
    isMediaFile?: boolean;
    mediaFileId?: string;
    status?: 'uploading' | 'processing' | 'ready' | 'failed';
  };
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);

  const { modelId, documentId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { models } = useAIModels();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isDefaultPromptActive, setIsDefaultPromptActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [limitMessage, setLimitMessage] = useState<string>("");
  const [showConfirmDeletePopup, setShowConfirmDeletePopup] = useState(false);
  const [conversationModel, setConversationModel] = useState<any>(null);
  const [conversationId, setConversationId] = useState<string | null>(
    documentId || null
  );
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const { isModelDropdownOpen, setModelDropdownOpen } = useUIStore();
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(
    null
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewMarkdown, setPreviewMarkdown] = useState<{ content: string; filename: string } | null>(null);

  // Auto-scroll to bottom only when user sends a new message (not on initial load)
  const prevMessagesLength = useRef(messages.length);
  useEffect(() => {
    // Only scroll when messages are added (user sent new message or bot responded)
    // Don't scroll on initial load or when loading existing conversation
    if (messages.length > prevMessagesLength.current && prevMessagesLength.current > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevMessagesLength.current = messages.length;
  }, [messages.length]);

  const COPY_TIMEOUT_MS = 2000;

  useEffect(() => {
    if (copiedMessageIndex !== null) {
      const timer = setTimeout(() => {
        setCopiedMessageIndex(null);
      }, COPY_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
  }, [copiedMessageIndex]);

  const selectedModel = models.find((model) => model.id === Number(modelId));

  const imageGeneratorModel = models.find(
    (model) => model.supports_image_generation === true
  );
  const imageGeneratorDocumentId = imageGeneratorModel?.documentId;

  const defaultPrompt = selectedModel?.user_prompt || "";

  // Track the last fetched documentId to prevent duplicate fetches
  const lastFetchedDocumentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!documentId) return;

    // Skip if already fetched for this documentId
    if (lastFetchedDocumentIdRef.current === documentId) return;

    let isCancelled = false;

    const fetchConversationMessages = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${baseUrl}/chat/conversation/${documentId}`,
          {
            headers: { Authorization: `Bearer ${storage.getJwtToken()}` },
          }
        );

        // Don't update state if cancelled or documentId changed
        if (isCancelled) return;

        // Mark as fetched only after successful response
        lastFetchedDocumentIdRef.current = documentId;

        const conv = response.data.data?.conversation;
        console.log("==>>> conv", conv);
        setConversationModel(conv?.ai_model ?? null);
        setConversationId(conv?.documentId ?? null);
        const msgsRaw = conv?.chat_messages ?? [];
        const normalised: Message[] = msgsRaw.map((item: any) => ({
          role: item.sender === "user" ? "user" : "assistant",
          content: item.message || "",
          attachments: item.attachments || [],
          documentId: item.documentId,
          message_type: item.message_type || "text",
          images: item.images || [],
          media_files: item.media_files || [],
        }));

        setMessages(normalised.reverse());
      } catch (error: any) {
        if (isCancelled) return;
        console.error("Error fetching conversation messages:", error);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchConversationMessages();

    return () => {
      isCancelled = true;
    };
  }, [documentId]);

  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        await handleFiles(e.dataTransfer.files);
      }
    };

    const dropArea = dropAreaRef.current;
    if (dropArea) {
      dropArea.addEventListener("dragover", handleDragOver);
      dropArea.addEventListener("dragleave", handleDragLeave);
      dropArea.addEventListener("drop", handleDrop as any);
    }

    return () => {
      if (dropArea) {
        dropArea.removeEventListener("dragover", handleDragOver);
        dropArea.removeEventListener("dragleave", handleDragLeave);
        dropArea.removeEventListener("drop", handleDrop);
      }
    };
  }, []);

  // Helper to check if file is a document (PDF, etc.) that needs RAG processing
  const isDocumentFile = (mimeType: string): boolean => {
    const documentTypes = [
      'application/pdf',
      'text/plain',
      'text/markdown',
      'text/x-markdown',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return documentTypes.includes(mimeType);
  };

  // Helper to check if file is an audio/video that needs Gemini processing
  const isAudioVideoFile = (mimeType: string): boolean => {
    return mimeType.startsWith('audio/') || mimeType.startsWith('video/');
  };

  // Helper to check if file should go through /api/media/upload (for RAG processing)
  // This includes documents (PDF, markdown, text) and audio/video files
  const shouldUploadToMediaApi = (mimeType: string): boolean => {
    return isDocumentFile(mimeType) || isAudioVideoFile(mimeType);
  };

  // Get current model (conversation model takes priority over selected model)
  const getCurrentModel = () => {
    return conversationModel || selectedModel;
  };

  // Validate if file type is supported by current model
  const validateFileType = (file: File): { valid: boolean; reason?: string } => {
    const model = getCurrentModel();
    if (!model) {
      return { valid: false, reason: "No model selected" };
    }

    const mimeType = file.type;

    // Check image support
    if (mimeType.startsWith('image/')) {
      if (!model.supports_image) {
        return { valid: false, reason: `${model.title} does not support image files` };
      }
      return { valid: true };
    }

    // Check PDF/document support
    if (mimeType === 'application/pdf') {
      if (!model.supports_pdf) {
        return { valid: false, reason: `${model.title} does not support PDF files` };
      }
      return { valid: true };
    }

    // Check audio support
    if (mimeType.startsWith('audio/')) {
      if (!model.supports_audio) {
        return { valid: false, reason: `${model.title} does not support audio files` };
      }
      return { valid: true };
    }

    // Check video support
    if (mimeType.startsWith('video/')) {
      if (!model.supports_video) {
        return { valid: false, reason: `${model.title} does not support video files` };
      }
      return { valid: true };
    }

    // Other document types (markdown, text) - assume supported if PDF is supported
    if (isDocumentFile(mimeType)) {
      if (!model.supports_pdf) {
        return { valid: false, reason: `${model.title} does not support document files` };
      }
      return { valid: true };
    }

    // Unknown file type
    return { valid: false, reason: "Unsupported file type" };
  };

  // State for showing unsupported file error
  const [unsupportedFileError, setUnsupportedFileError] = useState<string | null>(null);

  const handleFiles = async (files: FileList) => {
    const imageFiles: File[] = [];
    const documentFiles: File[] = [];
    const rejectedFiles: { name: string; reason: string }[] = [];

    // Validate and separate files by type
    Array.from(files).forEach((file) => {
      const validation = validateFileType(file);
      if (!validation.valid) {
        rejectedFiles.push({ name: file.name, reason: validation.reason || "Unsupported" });
        return;
      }

      if (shouldUploadToMediaApi(file.type)) {
        documentFiles.push(file);
      } else {
        imageFiles.push(file);
      }
    });

    // Show error for rejected files
    if (rejectedFiles.length > 0) {
      const errorMessage = rejectedFiles.length === 1
        ? rejectedFiles[0].reason
        : `${rejectedFiles.length} files not supported: ${rejectedFiles.map(f => f.name).join(', ')}`;
      setUnsupportedFileError(errorMessage);
      // Auto-hide after 5 seconds
      setTimeout(() => setUnsupportedFileError(null), 5000);
    }

    // Upload images to Strapi /api/upload (existing flow)
    if (imageFiles.length > 0) {
      const formData = new FormData();
      imageFiles.forEach((file) => {
        formData.append("files", file);
      });

      try {
        const res = await axios.post(`${baseUrl}/upload`, formData, {
          headers: { Authorization: `Bearer ${storage.getJwtToken()}` },
        });
        const uploadedFiles = res.data.map((item: any) => ({
          type: item.mime,
          url: item.formats?.thumbnail?.url
            ? item.formats.thumbnail.url.startsWith("http")
              ? item.formats.thumbnail.url
              : baseHost + item.formats.thumbnail.url
            : item.url.startsWith("http")
            ? item.url
            : baseHost + item.url,
          name: item.name,
          id: item.id,
          documentId: item.documentId,
          isMediaFile: false,
          status: 'ready' as const,
        }));
        setAttachments((prev) => [...prev, ...uploadedFiles]);
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }

    // Upload documents/audio/video to /api/media/upload (RAG processing)
    for (const file of documentFiles) {
      // Add placeholder with uploading status
      const tempId = Date.now() + Math.random();
      const placeholderFile: UploadedFile = {
        type: file.type,
        url: '',
        name: file.name,
        id: tempId,
        documentId: '',
        isMediaFile: true,
        status: 'uploading',
      };
      setAttachments((prev) => [...prev, placeholderFile]);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const res = await axios.post(`${baseUrl}/media/upload`, formData, {
          headers: {
            Authorization: `Bearer ${storage.getJwtToken()}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        if (res.data.success) {
          // Update placeholder with real data
          setAttachments((prev) =>
            prev.map((att) =>
              att.id === tempId
                ? {
                    ...att,
                    id: res.data.data.id,
                    documentId: res.data.data.id,
                    mediaFileId: res.data.data.id,
                    status: res.data.data.ready ? 'ready' : 'processing',
                  }
                : att
            )
          );

          // If still processing, poll for status
          if (!res.data.data.ready) {
            pollMediaStatus(res.data.data.id, tempId);
          }
        }
      } catch (error: any) {
        console.error("Document upload failed:", error);
        // Update placeholder to failed
        setAttachments((prev) =>
          prev.map((att) =>
            att.id === tempId
              ? { ...att, status: 'failed' as const }
              : att
          )
        );
      }
    }
  };

  // Poll media file status until ready
  const pollMediaStatus = async (mediaFileId: string, tempId: number) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await axios.get(`${baseUrl}/media/status/${mediaFileId}`, {
          headers: { Authorization: `Bearer ${storage.getJwtToken()}` },
        });

        if (res.data.data.status === 'active' || res.data.data.rag_status === 'completed') {
          setAttachments((prev) =>
            prev.map((att) =>
              att.id === tempId || att.mediaFileId === mediaFileId
                ? { ...att, status: 'ready' as const }
                : att
            )
          );
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        }
      } catch (error) {
        console.error("Error polling media status:", error);
      }
    };

    poll();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files) {
      await handleFiles(e.target.files);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearChat = () => {
    setMessages([]);
    setShowMenu(false);
    setShowUploadMenu(false);
  };

  const handleCopyMessage = (index: number, content: string) => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopiedMessageIndex(index);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  // Open media file using signed URL
  const handleOpenMediaFile = async (mediaFile: MediaFile) => {
    try {
      const response = await axios.get(
        `${baseUrl}/media/${mediaFile.documentId}/signed-url`,
        {
          headers: { Authorization: `Bearer ${storage.getJwtToken()}` },
        }
      );

      const signedUrl = response.data?.data?.signed_url;
      if (signedUrl) {
        // For markdown files, fetch content and render in modal with Mermaid support
        if (mediaFile.media_type === 'markdown') {
          try {
            const contentResponse = await fetch(signedUrl);
            const markdownContent = await contentResponse.text();
            setPreviewMarkdown({
              content: markdownContent,
              filename: mediaFile.original_filename
            });
          } catch (fetchError) {
            console.error("Failed to fetch markdown content:", fetchError);
            // Fallback to opening in new tab
            window.open(signedUrl, '_blank');
          }
        } else {
          window.open(signedUrl, '_blank');
        }
      }
    } catch (error: any) {
      console.error("Failed to get signed URL:", error);
      // If URL expired (410 Gone), try to refresh
      if (error.response?.status === 410) {
        try {
          const refreshResponse = await axios.post(
            `${baseUrl}/media/${mediaFile.documentId}/refresh-url`,
            {},
            {
              headers: { Authorization: `Bearer ${storage.getJwtToken()}` },
            }
          );
          const newSignedUrl = refreshResponse.data?.data?.signed_url;
          if (newSignedUrl) {
            // For markdown files, fetch content and render in modal
            if (mediaFile.media_type === 'markdown') {
              try {
                const contentResponse = await fetch(newSignedUrl);
                const markdownContent = await contentResponse.text();
                setPreviewMarkdown({
                  content: markdownContent,
                  filename: mediaFile.original_filename
                });
              } catch (fetchError) {
                console.error("Failed to fetch markdown content:", fetchError);
                window.open(newSignedUrl, '_blank');
              }
            } else {
              window.open(newSignedUrl, '_blank');
            }
          }
        } catch (refreshError) {
          console.error("Failed to refresh signed URL:", refreshError);
        }
      }
    }
  };

  // Get icon for media file type (using available icons)
  const getMediaFileIcon = (_mediaType: string): string => {
    // Use generic file icon for all types
    return '/assets/images/icon-file-attach.svg';
  };

  // Get badge color for media file type
  const getMediaFileBadge = (mediaType: string): { text: string; color: string } => {
    switch (mediaType) {
      case 'pdf':
        return { text: 'PDF', color: '#ef4444' };
      case 'audio':
        return { text: 'Audio', color: '#a855f7' };
      case 'video':
        return { text: 'Video', color: '#3b82f6' };
      case 'markdown':
        return { text: 'MD', color: '#22c55e' };
      default:
        return { text: 'File', color: '#6b7280' };
    }
  };

  const handleDownloadImage = async (message: Message) => {
    if (
      message.message_type === "image" &&
      message.images &&
      message.images.length > 0
    ) {
      const downloadSingleImage = async (imageUrl: string, index: number) => {
        try {
          const absoluteUrl = imageUrl.startsWith("http")
            ? imageUrl
            : `${baseHost}${imageUrl}`;

          const response = await fetch(absoluteUrl, { mode: "cors" });
          const blob = await response.blob();

          const blobUrl = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = blobUrl;

          const urlParts = absoluteUrl.split("/");
          const fileName =
            urlParts[urlParts.length - 1].split("?")[0] ||
            `generated-image-${index}.png`;
          a.download = fileName;
          a.style.display = "none";

          // Trigger download
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
          console.error("Error downloading image:", err);
        }
      };

      await Promise.all(
        message.images.map((img, idx) =>
          downloadSingleImage(img.formats?.medium?.url || img.url, idx)
        )
      );
    }
  };

  const downloadImageFromUrl = async (imageUrl: string) => {
    try {
      const absoluteUrl = imageUrl.startsWith("http")
        ? imageUrl
        : `${baseHost}${imageUrl}`;

      const response = await fetch(absoluteUrl, { mode: "cors" });
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;

      const urlParts = absoluteUrl.split("/");
      const fileName =
        urlParts[urlParts.length - 1].split("?")[0] || "download.png";
      a.download = fileName;
      a.style.display = "none";

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading image:", err);
    }
  };

  const handleDeleteChat = async () => {
    try {
      if (!documentId) {
        console.error("No document ID in URL");
        handleClearChat();
        navigate("/");
        return;
      }

      console.log("Deleting conversation with document ID:", documentId);

      const deleteUrl = `${baseUrl}/chat/conversation/${documentId}`;
      console.log("Delete URL:", deleteUrl);

      await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${storage.getJwtToken()}`,
        },
      });

      handleClearChat();
      navigate("/");
    } catch (error) {
      console.error("Error deleting conversation:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      handleClearChat();

      navigate("/");
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent duplicate submits while loading
    if (isLoading) return;
    if (!inputMessage.trim() && attachments.length === 0) return;

    // Check if any files are still uploading or processing
    const pendingFiles = attachments.filter(
      (f) => f.status === 'uploading' || f.status === 'processing'
    );
    if (pendingFiles.length > 0) {
      alert('Please wait for files to finish uploading/processing');
      return;
    }

    // Check if any files failed
    const failedFiles = attachments.filter((f) => f.status === 'failed');
    if (failedFiles.length > 0) {
      const proceed = window.confirm(
        'Some files failed to upload. Do you want to send the message without them?'
      );
      if (!proceed) return;
      // Remove failed files
      setAttachments((prev) => prev.filter((f) => f.status !== 'failed'));
    }

    const formData = new FormData();
    formData.append("message", inputMessage);

    const modelIdToUse = (
      selectedModel?.documentId ||
      conversationModel?.documentId ||
      ""
    ).toString();
    formData.append("model_id", modelIdToUse);

    const userData = storage.getUserData();
    const userId = userData?.id ? userData.id.toString() : "";
    formData.append("user_id", userId);
    if (conversationId) {
      formData.append("conversation_id", conversationId);
    }

    // Image files (uploaded to Strapi)
    const imageFileIds = attachments
      .filter((file) => file.type.startsWith("image/") && !file.isMediaFile)
      .map((file) => file.documentId);

    imageFileIds.forEach((docId, idx) => {
      formData.append(`image_file_ids[${idx}]`, docId);
    });

    // Media files (PDFs, audio, video - uploaded to /api/media/upload for RAG)
    const mediaFileIds = attachments
      .filter((file) => file.isMediaFile && file.mediaFileId)
      .map((file) => file.mediaFileId as string);

    mediaFileIds.forEach((mediaId, idx) => {
      formData.append(`media_file_ids[${idx}]`, mediaId);
    });

    attachments.forEach((file, index) => {
      formData.append(`attachments[${index}][url]`, file.url || '');
      formData.append(`attachments[${index}][type]`, file.type);
      formData.append(`attachments[${index}][name]`, file.name);
      formData.append(`attachments[${index}][id]`, file.id.toString());
      formData.append(`attachments[${index}][documentId]`, file.documentId || '');
      formData.append(`attachments[${index}][isMediaFile]`, String(file.isMediaFile || false));
      if (file.mediaFileId) {
        formData.append(`attachments[${index}][mediaFileId]`, file.mediaFileId);
      }
    });

    const attachmentPreviews = attachments.map((file) => ({
      type: file.type,
      url: file.url,
      name: file.name,
    }));

    const userMessage: Message = {
      role: "user",
      content: inputMessage,
      attachments: attachmentPreviews,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsDefaultPromptActive(false);
    setAttachments([]);
    setIsLoading(true);

    try {
      const isImageGenerator = modelIdToUse === selectedModel?.documentId;

      const response = await axios.post(
        `${baseUrl}/chat/send-message`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${storage.getJwtToken()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!conversationId && response.data?.data?.conversation?.documentId) {
        setConversationId(response.data.data.conversation.documentId);
      }

      const assistantMessage: Message = {
        role: "assistant",
        content: response.data.data.message,
        message_type: response.data.data.message_type || "text",
        images: response.data.data.images || [],
        media_files: response.data.data.media_files || [],
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error("Error sending message:", error);

      // Extract error message from API response
      const errorMessage = error?.response?.data?.error?.message
        || error?.response?.data?.message
        || error?.message
        || "An error occurred";

      // Check if it's a limit/quota error (400 status with limit-related message)
      const isLimitError = error?.response?.status === 400 &&
        (errorMessage.toLowerCase().includes('limit') ||
         errorMessage.toLowerCase().includes('exceed') ||
         errorMessage.toLowerCase().includes('reached'));

      if (isLimitError) {
        setLimitMessage(errorMessage);
        setShowLimitPopup(true);
      } else {
        // For other errors, show as assistant message
        const errorAssistantMessage: Message = {
          role: "assistant",
          content: `⚠️ ${errorMessage}`,
          message_type: "text",
        };
        setMessages((prev) => [...prev, errorAssistantMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate("/pricing");
    setShowLimitPopup(false);
  };

  const LimitChatContent = () => (
    <>
      <div className=" text-white">
        {limitMessage ? (
          <>
            <span className="block mb-2">⚠️ {limitMessage}</span>
            <span className="block text-sm opacity-80">
              Please upgrade to continue using all features.
            </span>
          </>
        ) : (
          <>
            Your activities have been limited because: <br />
            💬 3 chats at max <br />❓ 10 questions at max <br /> Please upgrade to
            Premium account to continue
          </>
        )}
      </div>
    </>
  );

  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (e.clipboardData && e.clipboardData.items) {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            e.preventDefault();
            const blob = items[i].getAsFile();
            if (blob) {
              const dt = new DataTransfer();
              dt.items.add(blob);
              await handleFiles(dt.files);
            }
          }
        }
      }
    };
    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, []);

  const handleUploadPhoto = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = (e) => {
      if (e.target && (e.target as HTMLInputElement).files) {
        handleFiles((e.target as HTMLInputElement).files as FileList);
      }
    };
    input.click();
    setShowUploadMenu(false);
  };

  const handleUploadFile = () => {
    fileInputRef.current?.click();
    setShowUploadMenu(false);
  };

  const location = useLocation();
  useEffect(() => {
    setModelDropdownOpen(false);
  }, [location.pathname, setModelDropdownOpen]);

  const handleSelectModel = (model: AIModel) => {
    console.log("Model selected:", model);

    setModelDropdownOpen(false);

    if (model.is_premium) {
      navigate("/pricing");
      return;
    }

    const token = storage.getJwtToken();
    if (!token) {
      navigate("/get-started");
      return;
    }

    if (messages.length === 0) {
      navigate(`/chat/${model.id}`);
    } else {
      if (
        window.confirm(
          "Changing models will start a new conversation. Continue?"
        )
      ) {
        navigate(`/chat/${model.id}`);
      }
    }
  };

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsTablet(tablet);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(event.target as Node)
      ) {
        setModelDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setModelDropdownOpen]);

  const autoResizeTextarea = () => {
    const textarea = chatInputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !isLoading
    ) {
      e.preventDefault();
      if (formRef.current) {
        formRef.current.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        );
      }
    }
  };

  // State to cache signed URLs for preview
  const [signedUrlCache, setSignedUrlCache] = useState<Record<string, string>>({});

  function renderMarkdownWithMermaid(content: string, message: Message) {
    // Case 1: Image message with images array populated (Strapi upload images)
    if (
      message.message_type === "image" &&
      message.images &&
      message.images.length > 0
    ) {
      return (
        <>
          <div className="mb-3 text-sm">{content}</div>
          <div className="flex flex-wrap gap-3">
            {message.images.map((image, idx) => (
              <>
                <img
                  key={idx}
                  src={image.formats?.medium?.url || image.url}
                  alt={image.message as string}
                  className="generated-image cursor-pointer"
                  onClick={() =>
                    setPreviewImageUrl(image.formats?.large?.url || image.url)
                  }
                />
              </>
            ))}
          </div>
        </>
      );
    }

    // Case 1.5: Image message with media_files (generated images with signed URLs)
    const imageMediaFiles = message.media_files?.filter(mf => mf.media_type === 'image') || [];
    if (message.message_type === "image" && imageMediaFiles.length > 0) {
      // Extract caption text (filter out URLs from content)
      const lines = content?.split('\n') || [];
      const textLines = lines.filter(line =>
        !line.trim().match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)/i) &&
        !line.trim().includes('/uploads/generated/') &&
        !line.trim().includes('/uploads/')
      ).join('\n').trim();

      return (
        <>
          {textLines && <div className="mb-3 text-sm">{textLines}</div>}
          <div className="flex flex-wrap gap-3">
            {imageMediaFiles.map((mediaFile, idx) => (
              <SignedImage
                key={mediaFile.documentId || idx}
                mediaFileDocumentId={mediaFile.documentId}
                alt={`AI generated ${idx + 1}`}
                className="generated-image cursor-pointer max-w-full rounded-xl"
                style={{ maxHeight: '400px', objectFit: 'contain' }}
                onClick={() => {
                  // Use cached signed URL for preview if available
                  const cachedUrl = signedUrlCache[mediaFile.documentId];
                  if (cachedUrl) {
                    setPreviewImageUrl(cachedUrl);
                  }
                }}
                onSignedUrlReady={(url) => {
                  setSignedUrlCache(prev => ({ ...prev, [mediaFile.documentId]: url }));
                }}
              />
            ))}
          </div>
        </>
      );
    }

    // Case 2: Image message where content IS the image URL (legacy generated images without media_files)
    // Also handles text messages that contain image URLs (legacy data)
    if (content) {
      // Check if content contains image URLs (either pure image or text with embedded URLs)
      const lines = content.split('\n');
      const imageUrls = lines.filter(line =>
        line.trim().match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)/i) ||
        line.trim().includes('/uploads/generated/')
      );

      // Only use direct URLs if NO media_files are attached (legacy data)
      if (imageUrls.length > 0 && imageMediaFiles.length === 0) {
        // Extract non-URL text for caption (if any)
        const textLines = lines.filter(line =>
          !line.trim().match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)/i) &&
          !line.trim().includes('/uploads/generated/')
        ).join('\n').trim();

        return (
          <>
            {textLines && <div className="mb-3 text-sm">{textLines}</div>}
            <div className="flex flex-wrap gap-3">
              {imageUrls.map((url, idx) => (
                <img
                  key={idx}
                  src={url.trim()}
                  alt={`AI generated ${idx + 1}`}
                  className="generated-image cursor-pointer max-w-full rounded-xl"
                  style={{ maxHeight: '400px', objectFit: 'contain' }}
                  onClick={() => setPreviewImageUrl(url.trim())}
                />
              ))}
            </div>
          </>
        );
      }
    }

    // Check if content is HTML (legacy messages converted by backend)
    // New messages are stored as raw markdown for proper LaTeX, code, mermaid rendering
    const isHtmlContent = /<(p|div|h[1-6]|ul|ol|li|table|blockquote|pre)\b/i.test(content);

    if (isHtmlContent) {
      // Legacy: Render pre-converted HTML from old backend (before 2025-12-17)
      // HtmlContentRenderer handles mermaid/markmap blocks embedded in HTML
      return (
        <HtmlContentRenderer
          html={content}
          onMermaid={(chart) => <MermaidRenderer chart={chart} />}
          onMarkmap={(markdown) => <MarkmapRenderer markdown={markdown} />}
        />
      );
    }

    // Primary path: Parse raw markdown on frontend with full feature support
    // - LaTeX math: $...$ inline, $$...$$ block (via remark-math + rehype-katex)
    // - Code blocks: syntax highlighting with CodeBlock component
    // - Diagrams: mermaid, markmap
    // - Tables, lists, links, etc. (via remark-gfm)

    // Convert LaTeX delimiters: \(...\) → $...$ and \[...\] → $$...$$
    // This is needed because AI models (especially Gemini) output \(...\) format
    const convertedContent = convertLatexDelimiters(content);
    const blocks = convertedContent.split(/```(.*?)```/gs);

    const elements = [];

    for (let i = 0; i < blocks.length; i++) {
      if (i % 2 === 0) {
        // Skip empty blocks (trailing newlines, empty content)
        const trimmedBlock = blocks[i].trim();
        if (!trimmedBlock) continue;

        elements.push(
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              // Headings with tighter spacing
              h1: ({ node, ...props }) => (
                <h1 className="text-2xl font-bold mt-4 mb-2 first:mt-0" {...props} />
              ),
              h2: ({ node, ...props }) => (
                <h2 className="text-xl font-bold mt-3 mb-1.5 first:mt-0" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-lg font-bold mt-2.5 mb-1 first:mt-0" {...props} />
              ),
              h4: ({ node, ...props }) => (
                <h4 className="text-base font-bold mt-2 mb-1 first:mt-0" {...props} />
              ),
              // Paragraphs with compact spacing
              p: ({ node, ...props }) => (
                <p className="my-1.5 leading-relaxed" {...props} />
              ),
              // Lists with tighter spacing
              ul: ({ node, ...props }) => (
                <ul className="list-disc list-inside my-1.5 space-y-0.5" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal list-inside my-1.5 space-y-0.5" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="leading-relaxed" {...props} />
              ),
              // Blockquote styling
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-gray-500 pl-3 my-2 italic text-gray-300" {...props} />
              ),
              // Inline code
              code: ({ node, inline, ...props }: any) => (
                inline
                  ? <code className="bg-gray-800 px-1 py-0.5 rounded text-sm" {...props} />
                  : <code {...props} />
              ),
              // Links
              a: ({ node, ...props }) => (
                <a className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
              ),
              // Strong/Bold
              strong: ({ node, ...props }) => (
                <strong className="font-bold" {...props} />
              ),
              // Horizontal rule
              hr: ({ node, ...props }) => (
                <hr className="my-3 border-gray-600" {...props} />
              ),
              // Tables
              table: ({ node, ...props }) => (
                <table
                  className="table-auto border-collapse border border-[#606169] w-full my-2"
                  {...props}
                />
              ),
              th: ({ node, ...props }) => (
                <th
                  className="border border-[#606169] px-2 py-1 bg-[#2F3036] text-white"
                  {...props}
                />
              ),
              td: ({ node, ...props }) => (
                <td className="border border-[#606169] px-2 py-1" {...props} />
              ),
            }}>
            {trimmedBlock}
          </ReactMarkdown>
        );
      } else {
        const [lang, ...rest] = blocks[i].split("\n");
        const code = rest.join("\n");
        const trimmedLang = lang.trim().toLowerCase();

        if (trimmedLang === "mermaid") {
          elements.push(<MermaidRenderer chart={code} key={i} />);
        } else if (trimmedLang === "markmap") {
          elements.push(<MarkmapRenderer markdown={code} key={i} />);
        } else if (trimmedLang === "tikz" || /\\begin\{tikzpicture\}/.test(code) || /\\begin\{axis\}/.test(code)) {
          // TikZ diagram: explicit tikz tag OR contains tikzpicture/axis environment
          elements.push(<TikzRenderer code={code} key={i} />);
        } else {
          // Use CodeBlock for syntax highlighting + copy button
          elements.push(
            <CodeBlock
              key={i}
              code={code}
              language={lang.trim()}
            />
          );
        }
      }
    }

    return <>{elements}</>;
  }

  useEffect(() => {
    autoResizeTextarea();
  }, [inputMessage, defaultPrompt, isDefaultPromptActive]);

  return (
    <div className="flex flex-col flex-1 h-full min-h-0 relative">
      <style dangerouslySetInnerHTML={{ __html: loadingDotsStyle }} />

      {/* Toast for unsupported file type error */}
      {unsupportedFileError && (
        <div
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] animate-fade-in"
          style={{
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div className="flex items-center gap-3 px-4 py-3 bg-red-900/90 border border-red-500 rounded-lg shadow-lg max-w-md">
            <span className="text-yellow-400 text-lg">⚠️</span>
            <span className="text-white text-sm">{unsupportedFileError}</span>
            <button
              onClick={() => setUnsupportedFileError(null)}
              className="ml-2 text-white/60 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Fixed Header */}
      <div
        className="fixed top-4 mr-4 z-50"
        style={{ width: "-webkit-fill-available" }}
        css={css`
          @media (max-width: 1024px) {
            margin-left: 16px;
          }
        `}>
        <div
          className="flex items-center justify-between p-4 w-full bg-grey rounded-xl"
          css={css`
            @media (max-width: 1024px) {
              padding-left: 48px;
            }
          `}>
          <div className="flex items-center gap-2 relative">
            <h3 className="text-xl font-bold text-white">
              {conversationModel?.title || selectedModel?.title}
            </h3>
            <div className="z-50" ref={modelDropdownRef}>
              {messages.length === 0 && (
                <img
                  src="/assets/images/icon-arrow-dropdown.svg"
                  alt="select model"
                  className={`w-6 h-6 cursor-pointer transform transition-transform ${
                    isModelDropdownOpen ? "rotate-180" : ""
                  }`}
                  onClick={() => setModelDropdownOpen(!isModelDropdownOpen)}
                />
              )}

              {isModelDropdownOpen && (
                <>
                  {isMobile ? (
                    <>
                      <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-[90]"
                        onMouseDown={() => setModelDropdownOpen(false)}></div>
                      <div
                        className="fixed inset-0 z-[100] bg-grey overflow-y-auto p-4"
                        css={css`
                          border-radius: 0;
                          padding-top: 16px;
                          background-color: #2f3036;
                        `}>
                        <div className="flex items-center justify-between sticky top-0">
                          <div className="w-6 h-6"></div>
                          <h2 className="text-xl font-bold text-white text-center">
                            Select model
                          </h2>
                          <div className="p-2 rounded-full hover:bg-gray-700 transition-colors">
                            <img
                              src="/assets/images/icon-close.svg"
                              alt="Close"
                              className="w-6 h-6 cursor-pointer"
                              onClick={() => setModelDropdownOpen(false)}
                            />
                          </div>
                        </div>
                        <div className="pb-20">
                          <CardModel
                            listStyle={true}
                            onSelectModel={handleSelectModel}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      className="absolute left-[-16px] right-0 top-10 z-[100] bg-grey rounded-xl shadow-lg overflow-y-auto p-4"
                      css={css`
                        height: 600px;
                        width: 400px;
                        padding-top: 0;
                        background-color: #2f3036;
                      `}>
                      <CardModel
                        listStyle={true}
                        onSelectModel={handleSelectModel}
                      />
                    </div>
                  )}
                </>
              )}
            </div>{" "}
          </div>
          <div className="relative">
            <img
              src="/assets/images/icon-more.svg"
              alt=""
              className="w-6 h-6 cursor-pointer"
              onClick={() => setShowMenu(!showMenu)}
            />
            {showMenu && (
              <>
                <div
                  className="fixed top-0 left-0 w-full h-full z-10"
                  onClick={() => setShowMenu(false)}></div>
                <div
                  className="absolute z-20 bg-grey p-2 rounded-xl"
                  style={{
                    width: "180px",
                    right: "0",
                    marginTop: "16px",
                  }}>
                  <UIButton
                    text={"Delete Chat"}
                    textColor={"text-white"}
                    onClick={() => setShowConfirmDeletePopup(true)}
                    classButton={
                      "w-full text-left flex justify-between items-center bg-black pa-13 font-light rounded-xl p-4"
                    }
                    icon={"/assets/images/icon-trash.svg"}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Chat Area - dynamic margin based on attachments and Quick Actions */}
      <div
        className={`flex flex-col flex-1 overflow-y-auto w-full min-h-0 lg:px-52 px-4 lg:pt-24 chat-scroll-area ${
          // Check if any file has Quick Actions panel visible (ready + audio/video/pdf)
          attachments.some(f =>
            f.status === 'ready' &&
            f.isMediaFile &&
            (f.type.startsWith('audio/') || f.type.startsWith('video/') || f.type === 'application/pdf')
          ) ? 'mb-[380px]' : // Extra space for Quick Actions panel
          attachments.length > 0 ? 'mb-[220px]' : 'mb-[148px]'
        }`}
>
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-chat bg-black">
            <img
              src="/assets/images/logo.svg"
              alt="Chat Bot"
              className="w-16 h-16"
            />
            <p className="text-base font-bold text-center text-chat">
              Please share 🙌 what's <br />
              on your mind 💬.
            </p>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div className="text-center mb-2 pt-4">
              <p className="sm:text-xs md:text-sm text-chat uppercase tracking-wide">
                {conversationModel?.title || selectedModel?.title} | today{" "}
                {new Date().toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
            <div className="w-full">
              {messages.map((message, index) => (
                <div key={index} className="w-full">
                  <div className="flex w-full">
                    <div
                      key={index}
                      className={`flex w-full ${
                        message.role === "user"
                          ? "justify-end"
                          : "justify-start"
                      }`}>
                      <div
                        className={`flex items-start gap-3 p-3 w-full rounded-xl ${
                          message.role === "user" ? "bg-light-black" : ""
                        }`}>
                        <img
                          src={
                            message.role === "assistant"
                              ? "/assets/images/icon-chat.svg"
                              : "/assets/images/icon-avatar.svg"
                          }
                          alt={message.role === "assistant" ? "MixxAI" : "You"}
                          className="w-8 h-8 object-contain"
                        />
                        <div className="flex flex-col gap-1 w-full">
                          <span className="text-base text-white font-bold">
                            {message.role === "assistant" ? "MixxAI" : "You"}
                          </span>

                          {message.attachments &&
                            message.attachments.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {message.attachments.map((attachment, idx) => {
                                  // Get badge based on attachment type
                                  const getAttachmentBadge = () => {
                                    if (attachment.type === 'application/pdf') {
                                      return { text: 'PDF', color: '#ef4444', icon: '/assets/images/icon-pdf.svg' };
                                    } else if (attachment.type.startsWith('audio/')) {
                                      return { text: 'Audio', color: '#a855f7', icon: '/assets/images/icon-audio.svg' };
                                    } else if (attachment.type.startsWith('video/')) {
                                      return { text: 'Video', color: '#3b82f6', icon: '/assets/images/icon-video.svg' };
                                    } else if (attachment.type === 'text/markdown' || attachment.type === 'text/x-markdown') {
                                      return { text: 'MD', color: '#22c55e', icon: '/assets/images/icon-file-attach.svg' };
                                    }
                                    return { text: 'File', color: '#6b7280', icon: '/assets/images/icon-file-attach.svg' };
                                  };

                                  const badge = getAttachmentBadge();

                                  return (
                                    <div key={idx}>
                                      {attachment.type.startsWith("image/") ? (
                                        <img
                                          src={attachment.url}
                                          alt={`Attachment ${idx}`}
                                          className="attachment-preview"
                                        />
                                      ) : (
                                        <div
                                          className="flex items-center p-2 rounded-xl"
                                          style={{ border: "1px solid #606169" }}>
                                          <div
                                            className="w-10 h-10 rounded-lg flex items-center justify-center mr-2"
                                            style={{ backgroundColor: badge.color + '20' }}>
                                            {attachment.type.startsWith('video/') ? (
                                              <span className="text-lg">🎬</span>
                                            ) : attachment.type.startsWith('audio/') ? (
                                              <span className="text-lg">🎵</span>
                                            ) : attachment.type === 'application/pdf' ? (
                                              <span className="text-white text-xs font-bold">PDF</span>
                                            ) : (
                                              <span className="text-white text-xs font-bold">{badge.text}</span>
                                            )}
                                          </div>
                                          <div className="flex flex-col">
                                            <span
                                              className="text-sm text-white truncate"
                                              style={{ maxWidth: "150px" }}>
                                              {attachment.name}
                                            </span>
                                            <span
                                              className="text-xs font-medium"
                                              style={{ color: badge.color }}>
                                              {badge.text}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                          {/* Media Files (PDF, Audio, Video, Markdown) - exclude images since they're rendered inline */}
                          {message.media_files &&
                            message.media_files.filter(mf => mf.media_type !== 'image').length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {message.media_files
                                  .filter(mf => mf.media_type !== 'image')
                                  .map((mediaFile, idx) => {
                                  const badge = getMediaFileBadge(mediaFile.media_type);
                                  return (
                                    <div
                                      key={idx}
                                      className="flex items-center p-2 rounded-xl cursor-pointer hover:bg-gray-700 transition-colors"
                                      style={{ border: "1px solid #606169" }}
                                      onClick={() => handleOpenMediaFile(mediaFile)}
                                    >
                                      <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center mr-2"
                                        style={{ backgroundColor: badge.color + '20' }}>
                                        {mediaFile.media_type === 'video' ? (
                                          <span className="text-lg">🎬</span>
                                        ) : mediaFile.media_type === 'audio' ? (
                                          <span className="text-lg">🎵</span>
                                        ) : mediaFile.media_type === 'pdf' ? (
                                          <span className="text-white text-xs font-bold">PDF</span>
                                        ) : mediaFile.media_type === 'markdown' ? (
                                          <span className="text-white text-xs font-bold">MD</span>
                                        ) : (
                                          <span className="text-white text-xs font-bold">{badge.text}</span>
                                        )}
                                      </div>
                                      <div className="flex flex-col">
                                        <span
                                          className="text-sm text-white truncate"
                                          style={{ maxWidth: "150px" }}>
                                          {mediaFile.original_filename}
                                        </span>
                                        <span
                                          className="text-xs font-medium"
                                          style={{ color: badge.color }}>
                                          {badge.text}
                                        </span>
                                      </div>
                                      <img
                                        src="/assets/images/icon-arrow-small.svg"
                                        alt="open"
                                        className="w-4 h-4 ml-2 opacity-50"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                          <div className="sm:text-sm md:text-base text-chat-white markdown-content">
                            {renderMarkdownWithMermaid(
                              message.content,
                              message
                            )}
                          </div>
                          {message.role === "assistant" && (
                            <div className="flex justify-between mt-2">
                              <div
                                className="flex items-center cursor-pointer"
                                onClick={() =>
                                  message.message_type === "image" &&
                                  message.images &&
                                  message.images.length > 0
                                    ? handleDownloadImage(message)
                                    : handleCopyMessage(index, message.content)
                                }>
                                <img
                                  className="w-4 h-4 mr-1"
                                  src={
                                    message.message_type === "image" &&
                                    message.images &&
                                    message.images.length > 0
                                      ? "/assets/images/icon-download.svg"
                                      : copiedMessageIndex === index
                                      ? "/assets/images/icon-copy-ok.svg"
                                      : "/assets/images/icon-copy.svg"
                                  }
                                  alt={
                                    message.message_type === "image" &&
                                    message.images &&
                                    message.images.length > 0
                                      ? "download"
                                      : copiedMessageIndex === index
                                      ? "copied"
                                      : "copy"
                                  }
                                />{" "}
                                <span className="text-xs font-bold text-color-premium-desc">
                                  {message.message_type === "image" &&
                                  message.images &&
                                  message.images.length > 0
                                    ? "Download"
                                    : copiedMessageIndex === index
                                    ? "Copied"
                                    : "Copy"}
                                </span>
                              </div>
                              <div
                                className="flex items-center cursor-pointer"
                                onClick={() =>
                                  navigate(`/report/${message.documentId}`)
                                }>
                                <img
                                  className="w-4 h-4"
                                  src="/assets/images/icon-report.svg"
                                  alt="icon-report"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="mb-4 mt-3 px-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
                      <img
                        src="/assets/images/icon-chat.svg"
                        alt="MixxAI"
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-base text-white font-bold mr-2">
                        MixxAI
                      </span>
                      <div className="flex items-center space-x-1">
                        <div className="loading-dot loading-dot-1"></div>
                        <div className="loading-dot loading-dot-2"></div>
                        <div className="loading-dot loading-dot-3"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      <div
        className={`p-4 pt-2 px-52 fixed bottom-0 left-0 right-0 z-50 pl-[522px] bg-black ${
          isModelDropdownOpen && isMobile ? "hidden" : ""
        }`}
        css={css`
          @media (max-width: 1024px) {
            padding: 8px;
            padding-left: 8px;
          }
          @media (min-width: 1440px) {
            padding-left: 630px;
          }
          /* Gradient fade at top to blend with chat content */
          &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 0;
            right: 0;
            height: 20px;
            background: linear-gradient(to bottom, transparent, black);
            pointer-events: none;
          }
        `}
        ref={dropAreaRef}>
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((file, index) =>
              file.type.startsWith("image/") && !file.isMediaFile ? (
                <div className="relative" key={index}>
                  <img
                    src={file.url}
                    alt={file.name}
                    className="attachment-preview"
                  />
                  <button
                    onClick={() => handleRemoveAttachment(index)}
                    className="absolute top-0 right-0">
                    <img
                      src="/assets/images/icon-close-small.svg"
                      alt="close"
                      className="w-5 h-5"
                      style={{ filter: "invert(1)" }}
                    />
                  </button>
                </div>
              ) : (
                <div
                  key={index}
                  className={`flex items-center p-2 rounded-xl relative ${
                    file.status === 'failed' ? 'border-red-500' : ''
                  }`}
                  style={{
                    border: file.status === 'failed'
                      ? "1px solid #ef4444"
                      : "1px solid #606169",
                    opacity: file.status === 'uploading' || file.status === 'processing' ? 0.7 : 1,
                  }}>
                  {/* File icon with processing animation */}
                  {file.type === 'application/pdf' ? (
                    <div className={`w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 ${
                      file.status === 'processing' ? 'status-processing' :
                      file.status === 'uploading' ? 'status-uploading' : ''
                    }`}>
                      <span className="text-white text-xs font-bold">PDF</span>
                    </div>
                  ) : file.type.startsWith('audio/') ? (
                    <div className={`w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mr-2 ${
                      file.status === 'processing' ? 'status-processing' :
                      file.status === 'uploading' ? 'status-uploading' : ''
                    }`}>
                      <span className="text-white text-lg">🎵</span>
                    </div>
                  ) : file.type.startsWith('video/') ? (
                    <div className={`w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-2 ${
                      file.status === 'processing' ? 'status-processing' :
                      file.status === 'uploading' ? 'status-uploading' : ''
                    }`}>
                      <span className="text-white text-lg">🎬</span>
                    </div>
                  ) : file.type === 'text/markdown' || file.type === 'text/x-markdown' ? (
                    <div className={`w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center mr-2 ${
                      file.status === 'processing' ? 'status-processing' :
                      file.status === 'uploading' ? 'status-uploading' : ''
                    }`}>
                      <span className="text-white text-xs font-bold">MD</span>
                    </div>
                  ) : file.type === 'text/plain' ? (
                    <div className={`w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-2 ${
                      file.status === 'processing' ? 'status-processing' :
                      file.status === 'uploading' ? 'status-uploading' : ''
                    }`}>
                      <span className="text-white text-xs font-bold">TXT</span>
                    </div>
                  ) : (
                    <img
                      src="/assets/images/icon-file-attach.svg"
                      alt="File"
                      className="w-8 h-8 mr-2"
                    />
                  )}
                  <div className="flex flex-col">
                    <span
                      className="text-sm text-white truncate"
                      style={{ maxWidth: "120px" }}>
                      {file.name}
                    </span>
                    <span className={`text-xs ${
                      file.status === 'ready' ? 'text-green-400' :
                      file.status === 'failed' ? 'text-red-400' :
                      file.status === 'processing' ? 'text-blue-400' :
                      file.status === 'uploading' ? 'text-yellow-400' :
                      'text-chat'
                    }`}>
                      {file.status === 'ready' ? '✓ Ready' :
                       file.status === 'failed' ? '✗ Failed' :
                       file.status === 'processing' ? 'Processing...' :
                       file.status === 'uploading' ? 'Uploading...' :
                       file.type === 'application/pdf' ? 'PDF Document' :
                       file.type.startsWith('audio/') ? 'Audio File' :
                       file.type.startsWith('video/') ? 'Video File' :
                       file.type === 'text/markdown' || file.type === 'text/x-markdown' ? 'Markdown' :
                       file.type === 'text/plain' ? 'Text File' :
                       'File'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveAttachment(index)}
                    className="ml-2"
                    disabled={file.status === 'uploading'}>
                    <img
                      src="/assets/images/icon-close-small.svg"
                      alt="close"
                      className="w-5 h-5"
                    />
                  </button>
                </div>
              )
            )}{" "}
          </div>
        )}

        {/* Quick Actions for audio/video/PDF files */}
        {attachments.some(f =>
          f.status === 'ready' &&
          f.isMediaFile &&
          (f.type.startsWith('audio/') || f.type.startsWith('video/') || f.type === 'application/pdf')
        ) && (
          <div className="mb-3">
            {attachments
              .filter(f =>
                f.status === 'ready' &&
                f.isMediaFile &&
                (f.type.startsWith('audio/') || f.type.startsWith('video/') || f.type === 'application/pdf')
              )
              .map((file, index) => (
                <MediaQuickActions
                  key={`quick-action-${index}`}
                  mediaType={
                    file.type.startsWith('audio/') ? 'audio' :
                    file.type.startsWith('video/') ? 'video' :
                    'pdf'
                  }
                  fileName={file.name}
                  onSelectAction={(action: QuickAction) => {
                    setInputMessage(action.prompt);
                    setIsDefaultPromptActive(false);
                    // Focus on input
                    chatInputRef.current?.focus();
                  }}
                  disabled={isLoading}
                />
              ))
            }
          </div>
        )}

        <form onSubmit={handleSendMessage} ref={formRef}>
          <div
            className="relative flex items-start justify-between overflow-visible p-[2px] rounded-[12px]"
            style={{
              background: "linear-gradient(97deg, #3AF062 0%, #231ADD 100%)",
            }}>
            <div className="flex-1 bg-black rounded-[10px] flex items-center relative">
              <textarea
                ref={chatInputRef}
                value={
                  isDefaultPromptActive && !inputMessage
                    ? defaultPrompt
                    : inputMessage
                }
                onChange={(e) => {
                  if (isDefaultPromptActive && !inputMessage) {
                    setIsDefaultPromptActive(false);
                  }
                  setInputMessage(e.target.value);
                  autoResizeTextarea();
                }}
                placeholder={
                  selectedModel?.documentId !== imageGeneratorDocumentId ||
                  !isDefaultPromptActive
                    ? "✨ Would you like to ask?"
                    : undefined
                }
                disabled={isLoading}
                className="text-sm font-light w-full resize-none bg-transparent rounded-xl outline-none pl-4 pt-4 pb-10"
                style={{
                  height: "92px",
                }}
                onKeyDown={handleKeyDown}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileInputChange}
                multiple
              />

              <div>
                <button
                  onClick={() => setShowUploadMenu(!showUploadMenu)}
                  type="button"
                  disabled={isLoading}
                  className={`absolute left-3 bottom-3 rounded-xl p-1 ${
                    showUploadMenu ? "bg-[rgba(255,255,255,0.20)]" : ""
                  }`}>
                  <img
                    src="/assets/images/icon-add.svg"
                    alt="add"
                    className="w-6 h-6"
                  />
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`absolute right-3 bottom-3 transition-opacity ${
                    isLoading ? 'opacity-40 cursor-not-allowed' : 'opacity-100 cursor-pointer hover:opacity-80'
                  }`}>
                  <img
                    src="/assets/images/icon-arrow-up.svg"
                    alt="send-message"
                    className="w-8 h-8"
                  />
                </button>
              </div>
              {showUploadMenu && (
                <>
                  <div
                    className="absolute top-0 left-0 w-full h-full"
                    onClick={() => setShowUploadMenu(false)}></div>
                  <div
                    className="absolute bottom-12 left-3 flex flex-col gap-2 items-stretch p-2 rounded-xl z-50"
                    style={{
                      width: "180px",
                      backgroundColor: "#2F3036",
                    }}>
                    <UIButton
                      text={"Upload Photos"}
                      textColor={"text-white"}
                      onClick={handleUploadPhoto}
                      classButton={
                        "w-full text-left flex justify-between items-center pa-13 font-light"
                      }
                      icon={"/assets/images/icon-upload-img.svg"}
                    />
                    <UIButton
                      text={"Upload Files"}
                      textColor={"text-white"}
                      onClick={handleUploadFile}
                      classButton={
                        "w-full text-left flex justify-between items-center pa-13 font-light"
                      }
                      icon={"/assets/images/icon-upload-file.svg"}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </form>
      </div>

      <UIPopup
        isOpen={showLimitPopup}
        onClose={() => {
          setShowLimitPopup(false);
          setLimitMessage("");
        }}
        title="Limit Reached"
        primaryButton={{
          text: "Upgrade",
          onClick: handleUpgrade,
          backgroundColor: "btn-color",
          textColor: "text-[#000]",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setShowLimitPopup(false);
            setLimitMessage("");
          },
          backgroundColor: "btn-disabled",
        }}>
        <LimitChatContent />
      </UIPopup>

      <UIPopup
        isOpen={showConfirmDeletePopup}
        onClose={() => setShowConfirmDeletePopup(false)}
        title="Delete Chat"
        primaryButton={{
          text: "Confirm",
          onClick: () => {
            handleDeleteChat();
            setShowConfirmDeletePopup(false);
          },
          backgroundColor: "btn-invalid",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setShowConfirmDeletePopup(false),
          backgroundColor: "btn-disabled",
        }}>
        <h4>
          Are you sure to delete this chat? This action can not be able to
          revert.
        </h4>
      </UIPopup>

      {/* Image Preview Modal */}
      {previewImageUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black">
          <div className="flex justify-start p-4">
            <button onClick={() => setPreviewImageUrl(null)}>
              <img
                src="/assets/images/icon-close.svg"
                alt="close"
                className="w-6 h-6"
              />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center overflow-auto">
            <img
              src={previewImageUrl}
              alt="preview"
              className="max-w-full max-h-full"
            />
          </div>

          <div className="p-4 flex gap-4 justify-center">
            <div style={{ width: "180px" }}>
              <UIButton
                text="Share"
                textColor="text-white"
                icon="/assets/images/icon-share-img.svg"
                onClick={() => {
                  navigator.clipboard
                    .writeText(previewImageUrl)
                    .catch((err) => console.error("Share copy failed:", err));
                }}
                classButton="flex justify-center items-center flex-row-reverse w-full bg-grey"
              />
            </div>

            <div style={{ width: "180px" }}>
              <UIButton
                text="Download"
                textColor="text-white"
                icon="/assets/images/icon-download-white.svg"
                onClick={() => downloadImageFromUrl(previewImageUrl)}
                classButton="flex justify-center items-center flex-row-reverse w-full bg-grey"
              />
            </div>
          </div>
        </div>
      )}

      {/* Markdown Preview Modal with Mermaid/Flowchart Support */}
      {previewMarkdown && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black">
          <div className="flex justify-between items-center p-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: '#22c55e', color: 'white' }}>
                MD
              </span>
              <h3 className="text-white font-medium truncate" style={{ maxWidth: '300px' }}>
                {previewMarkdown.filename}
              </h3>
            </div>
            <button onClick={() => setPreviewMarkdown(null)}>
              <img
                src="/assets/images/icon-close.svg"
                alt="close"
                className="w-6 h-6"
              />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <div className="prose prose-invert max-w-none">
              {renderMarkdownWithMermaid(previewMarkdown.content, { content: previewMarkdown.content, role: 'assistant', message_type: 'text' } as Message)}
            </div>
          </div>

          <div className="p-4 flex gap-4 justify-center border-t border-gray-700">
            <div style={{ width: "180px" }}>
              <UIButton
                text="Copy"
                textColor="text-white"
                icon="/assets/images/icon-copy.svg"
                onClick={() => {
                  navigator.clipboard
                    .writeText(previewMarkdown.content)
                    .catch((err) => console.error("Copy failed:", err));
                }}
                classButton="flex justify-center items-center flex-row-reverse w-full bg-grey"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
